<script lang="ts" module>
  import type { Component } from 'svelte';

  export interface MenuItem {
    id: string;
    label: string;
    onselect: () => void;
    icon?: Component<{ size?: number; 'aria-hidden'?: boolean | 'true' }>;
    /** Renders a checkbox (`menuitemcheckbox`) or radio (`menuitemradio`) item. */
    kind?: 'item' | 'checkbox' | 'radio';
    checked?: boolean;
    disabled?: boolean;
    danger?: boolean;
    separatorBefore?: boolean;
  }

  export interface MenuTriggerProps {
    id: string;
    'aria-haspopup': 'menu';
    'aria-expanded': boolean;
    'aria-controls': string;
    onclick: (event: MouseEvent) => void;
    onkeydown: (event: KeyboardEvent) => void;
  }
</script>

<script lang="ts">
  import Check from '@lucide/svelte/icons/check';
  import { tick, type Snippet } from 'svelte';

  interface Props {
    /** Accessible name of the menu. */
    label: string;
    items: MenuItem[];
    trigger: Snippet<[MenuTriggerProps]>;
    align?: 'start' | 'end';
  }

  let { label, items, trigger, align = 'end' }: Props = $props();

  const id = $props.id();
  let open = $state(false);
  let active = $state(0);
  let wrapper: HTMLElement | undefined = $state();
  let menu: HTMLElement | undefined = $state();

  const enabled = $derived(items.flatMap((item, i) => (item.disabled ? [] : [i])));

  function focusActive() {
    menu?.querySelectorAll<HTMLElement>('[role^="menuitem"]')[active]?.focus();
  }

  async function show(focus: 'first' | 'last') {
    open = true;
    active = (focus === 'first' ? enabled[0] : enabled.at(-1)) ?? 0;
    await tick();
    focusActive();
  }

  function hide(restoreFocus: boolean) {
    open = false;
    if (restoreFocus) document.getElementById(`${id}-trigger`)?.focus();
  }

  function move(delta: number) {
    const position = enabled.indexOf(active);
    const next = enabled[(position + delta + enabled.length) % enabled.length];
    if (next !== undefined) active = next;
    focusActive();
  }

  function onMenuKeydown(event: KeyboardEvent) {
    const keys: Record<string, () => void> = {
      ArrowDown: () => {
        move(1);
      },
      ArrowUp: () => {
        move(-1);
      },
      Home: () => {
        active = enabled[0] ?? 0;
        focusActive();
      },
      End: () => {
        active = enabled.at(-1) ?? 0;
        focusActive();
      },
      Escape: () => {
        hide(true);
      },
    };
    const action = keys[event.key];
    if (action) {
      event.preventDefault();
      action();
    } else if (event.key === 'Tab') {
      hide(false);
    }
  }

  function select(item: MenuItem) {
    hide(true);
    item.onselect();
  }

  const triggerProps: MenuTriggerProps = $derived({
    id: `${id}-trigger`,
    'aria-haspopup': 'menu',
    'aria-expanded': open,
    'aria-controls': `${id}-menu`,
    onclick: () => {
      if (open) hide(false);
      else void show('first');
    },
    onkeydown: (event: KeyboardEvent) => {
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        void show(event.key === 'ArrowDown' ? 'first' : 'last');
      }
    },
  });

  function onWindowPointerdown(event: PointerEvent) {
    if (open && event.target instanceof Node && !wrapper?.contains(event.target)) hide(false);
  }

  const roles = { item: 'menuitem', checkbox: 'menuitemcheckbox', radio: 'menuitemradio' } as const;
</script>

<svelte:window onpointerdown={onWindowPointerdown} />

<div class="relative inline-flex" bind:this={wrapper}>
  {@render trigger(triggerProps)}
  <div
    bind:this={menu}
    id="{id}-menu"
    role="menu"
    aria-label={label}
    tabindex="-1"
    hidden={!open}
    class={[
      'absolute top-full z-40 mt-1 min-w-52 rounded-xl border border-line bg-surface p-1 shadow-lg',
      align === 'end' ? 'right-0' : 'left-0',
    ]}
    onkeydown={onMenuKeydown}
  >
    {#each items as item, i (item.id)}
      {#if item.separatorBefore}
        <div role="separator" class="my-1 border-t border-line"></div>
      {/if}
      <button
        type="button"
        role={roles[item.kind ?? 'item']}
        aria-checked={item.kind === 'checkbox' || item.kind === 'radio'
          ? (item.checked ?? false)
          : undefined}
        aria-disabled={item.disabled ? true : undefined}
        tabindex={i === active ? 0 : -1}
        class={[
          'flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left text-sm outline-none hover:bg-surface-2 focus-visible:bg-surface-2 aria-disabled:cursor-not-allowed aria-disabled:opacity-50 pointer-coarse:min-h-11',
          item.danger ? 'text-danger' : 'text-ink',
        ]}
        onclick={() => {
          if (!item.disabled) select(item);
        }}
        onpointermove={() => {
          if (!item.disabled) active = i;
        }}
      >
        {#if item.icon}
          <item.icon size={16} aria-hidden="true" />
        {/if}
        <span class="flex-1">{item.label}</span>
        {#if item.checked}
          <Check size={16} aria-hidden="true" />
        {/if}
      </button>
    {/each}
  </div>
</div>
